import moment from 'moment'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { trackClientEvent } from 'analytics/analytics'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import BaseOfferPriceContent from './BaseOfferPriceContent'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import config from 'constants/config'

interface Props {
  duration?: number
  offer: App.BedbankOffer
  alternativeCheckIn?: string
  alternativeCheckOut?: string
  alternativePrice?: number
}

const ALTERNATIVE_DATE_FORMAT = 'DD MMM'

function BedbankHotelPriceContent({
  duration,
  offer,
  alternativeCheckIn,
  alternativeCheckOut,
  alternativePrice,
}: Props) {
  const intl = useIntl()
  const enableDurationSearch = !!config.LERE_ENABLE_CALENDAR_DURATION_FILTER

  const showAlternativeDates = !!(alternativeCheckIn && alternativeCheckOut && alternativePrice)
  const sell = useBedbankOfferSell(offer, duration ?? 0, [])

  useEffect(() => {
    if (!Number.isFinite(duration)) {
      trackClientEvent({
        action: `duration_not_finite::${duration}`,
        subject: 'BedbankHotelPriceContent',
        category: 'logging',
        type: 'operational',
      })
    }
  }, [duration])

  if (!enableDurationSearch) {
    return null
  }

  const propertyFees = sell?.propertyFees ?? 0
  const hotelPrice = sell?.price ?? 0
  const hotelNights = sell?.los ?? 0
  const hotelValue = sell?.value ?? 0
  const hotelDiscount = sell?.discountPercentage ?? 0

  if (!hotelPrice || !hotelNights || !hotelValue) {
    return null
  }

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelValue,
    hotelDiscount,
    rooms: [],
  })

  const captionText = showAlternativeDates ?
    `${moment(alternativeCheckIn).format(ALTERNATIVE_DATE_FORMAT)} - ${moment(alternativeCheckOut).format(ALTERNATIVE_DATE_FORMAT)} from` :
      intl.formatMessage(
        {
          id: 'FTEKXrq-86oK79Lb2q2B',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Duration in nights followed by "from" for bedbank hotel price caption',
        },
        { count: hotelNights },
      )

  return <BaseOfferPriceContent
    offer={offer}
    duration={hotelNights}
    captionText={captionText}
    memberPrice={0}
    price={totalPrices.totals.price}
    value={totalPrices.totals.value}
    discount={totalPrices.original.discount}
    showValueDiscount={totalPrices.original.discount > 0}
  />
}

export default BedbankHotelPriceContent
